<template>
  <v-container fluid class="fill-height bg-primary">
    <v-row justify="center" align="center">
      <v-col cols="12" sm="8" md="6" lg="4">
        <v-card class="pa-4" elevation="12">
          <v-card-title class="text-center text-h5 mb-4">
            <v-icon size="48" color="primary" class="mb-2">mdi-account-plus</v-icon>
            <div>{{ t.register.title }}</div>
          </v-card-title>

          <!-- Referral Info -->
          <v-alert
            v-if="referralCode && !referralError"
            type="success"
            variant="tonal"
            class="mb-4"
          >
            <div class="font-weight-bold">{{ t.register.invited }}</div>
            <div class="text-caption">{{ t.register.referral_code.replace('{code}', referralCode) }}</div>
          </v-alert>

          <v-alert
            v-if="referralError"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ referralError }}
          </v-alert>

          <v-card-text>
            <v-form ref="registerForm" @submit.prevent="submitRegistration">
              <v-text-field
                v-model="email"
                :label="t.register.email_address"
                type="email"
                prepend-inner-icon="mdi-email"
                :rules="emailRules"
                required
                variant="outlined"
                class="mb-3"
                :disabled="loading"
              />

              <v-text-field
                v-model="password"
                :label="t.register.password"
                :type="showPassword ? 'text' : 'password'"
                prepend-inner-icon="mdi-lock"
                :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append-inner="showPassword = !showPassword"
                :rules="passwordRules"
                required
                variant="outlined"
                class="mb-3"
                :disabled="loading"
              />

              <v-text-field
                v-model="confirmPassword"
                :label="t.register.confirm_password"
                :type="showPassword ? 'text' : 'password'"
                prepend-inner-icon="mdi-lock-check"
                :rules="confirmPasswordRules"
                required
                variant="outlined"
                class="mb-4"
                :disabled="loading"
              />

              <AntibotVerification ref="antibot" v-model:valid="antibotValid" />
              <v-checkbox
                v-model="agreeToTerms"
                density="compact"
                hide-details
                class="mb-3"
                :rules="[v => !!v || t.register.accept_terms_required]"
              >
                <template #label>
                  <span class="text-body-2">
                    {{ t.register.agree_with }}
                    <a href="/TermsAndConditions.pdf" target="_blank" @click.stop>{{ t.register.terms_and_conditions }}</a>
                  </span>
                </template>
              </v-checkbox>
              <v-btn
                type="submit"
                color="primary"
                size="large"
                block
                :loading="loading"
                :disabled="!agreeToTerms || !antibotValid"
              >
                {{ t.register.create_account }}
              </v-btn>
            </v-form>
          </v-card-text>

          <v-divider class="my-4" />

          <v-card-actions class="justify-center">
            <span class="text-body-2 text-grey">{{ t.register.have_account }}</span>
            <v-btn variant="text" color="primary" size="small" @click="goToLogin">
              {{ t.register.login }}
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Success Dialog -->
        <v-dialog v-model="successDialog" persistent max-width="400">
          <v-card>
            <v-card-title class="text-center">
              <v-icon color="success" size="64">mdi-check-circle</v-icon>
            </v-card-title>
            <v-card-text class="text-center">
              <div class="text-h6 mb-2">{{ t.register.account_created }}</div>
              <div class="text-body-2 text-grey">{{ t.register.welcome_message }}</div>
            </v-card-text>
            <v-card-actions class="justify-center">
              <v-btn color="primary" variant="flat" @click="goToUserDetails">
                {{ t.onboarding.continue }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="5000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { BASE_URL } from "@/composables/url_config"
import { t as tRef } from "@/composables/useLanguages"
import axios from 'axios'
import AntibotVerification from "@/components/AntibotVerification.vue"

export default {
  name: 'RegisterView',
  components: { AntibotVerification },
  data() {
    return {
      email: '',
      password: '',
      confirmPassword: '',
      showPassword: false,
      loading: false,
      referralCode: '',
      referralError: '',
      successDialog: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'error',
      agreeToTerms: false,
      antibotValid: false
    }
  },
  computed: {
    t() {
      return tRef.value
    },
    emailRules() {
      return [
        v => !!v || this.t.register.email_required,
        v => /.+@.+\..+/.test(v) || this.t.register.email_invalid
      ]
    },
    passwordRules() {
      return [
        v => !!v || this.t.register.password_required,
        v => (v && v.length >= 6) || this.t.register.password_min_length
      ]
    },
    confirmPasswordRules() {
      return [
        v => !!v || this.t.register.confirm_password_required,
        v => v === this.password || this.t.register.passwords_mismatch
      ]
    }
  },
  mounted() {
    console.log('Route query:', this.$route.query)
    this.referralCode = this.$route.query.ref || ''
    console.log('Referral code:', this.referralCode)
    if (!this.referralCode) {
      this.referralError = this.t.register.invalid_registration_link
    }
  },
  methods: {
    async submitRegistration() {
      const { valid } = await this.$refs.registerForm.validate()
      if (!valid) return

      if (this.password !== this.confirmPassword) {
        this.showError(this.t.register.passwords_mismatch)
        return
      }

      this.loading = true

      try {
        const antibotData = this.$refs.antibot ? this.$refs.antibot.getAntibotData() : {}
        const response = await axios.post(
          `${BASE_URL}/mlm/register_with_referral_api`,
          {
            email: this.email,
            password: this.password,
            referral_code: this.referralCode,
            website: antibotData.website || '',
            elapsed_ms: antibotData.elapsed_ms || 0
          }
        )

        if (response.data.error) {
          this.showError(response.data.message)
        } else {
          // Save token and user to store
          this.$store.commit('setToken', { token: response.data.token })
          this.$store.commit('setUser', { user: response.data.user })
          this.$store.commit('setLoggedIn', true)

          this.successDialog = true
        }
      } catch (err) {
        this.showError(this.t.register.registration_error)
        console.error('Registration error:', err)
      } finally {
        this.loading = false
      }
    },

    showError(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'error'
      this.snackbar = true
    },

    goToLogin() {
      this.$router.push('/homeLoggedOut')
    },

    goToUserDetails() {
      this.successDialog = false
      this.$router.push('/onboarding')
    }
  }
}
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
}
</style>
